import { useContext, useMemo } from 'react';

import { AppContext } from '../context/AppContext';
import type { HistoricSystemDataRecord } from '../types/charts';
import { hasLastTwoSocReadings } from '../utils/disconnectHelpers';

interface AppContextValues {
  twentyFourHoursOfData: HistoricSystemDataRecord[] | null;
  fetchingTwentyFourHoursOfData: boolean;
  fetchingBehaviourForecast: boolean;
  forecastIsStaleError: boolean;
}

export interface InverterConnectionStatus {
  /** True while the underlying forecast/system-data checks are still in flight. */
  isChecking: boolean;
  isConnected: boolean;
  /** True once checks complete and something looks wrong; unlike `isConnected`, stays false when there's simply no data yet. */
  looksDisconnected: boolean;
  forecastIsStaleError: boolean;
}

/** Shared connectivity signals for the dashboard banner and Settings status badge; each derives its own boolean. */
const useInverterConnectionStatus = (): InverterConnectionStatus => {
  const { twentyFourHoursOfData, fetchingTwentyFourHoursOfData, fetchingBehaviourForecast, forecastIsStaleError } =
    useContext(AppContext) as unknown as AppContextValues;

  return useMemo(() => {
    const isChecking = !!fetchingTwentyFourHoursOfData || !!fetchingBehaviourForecast;
    const hasRecentSocReadings = !!twentyFourHoursOfData?.length && !!hasLastTwoSocReadings(twentyFourHoursOfData);
    const socReadingsWereMissing = !!twentyFourHoursOfData?.length && !hasRecentSocReadings;

    const isConnected = hasRecentSocReadings && !forecastIsStaleError;
    const looksDisconnected = !isChecking && (!!forecastIsStaleError || socReadingsWereMissing);

    return { isChecking, isConnected, looksDisconnected, forecastIsStaleError };
  }, [twentyFourHoursOfData, fetchingTwentyFourHoursOfData, fetchingBehaviourForecast, forecastIsStaleError]);
};

export default useInverterConnectionStatus;
